// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import RoundGameInfoModal from '@/components/RoundGameInfoModal';
import { useUser } from '@/app/user-context';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import { ladderForWeight, meritRowForWeight } from '@/features/game-definitions/merits';
import { useGameMeritLadders } from '@/features/game-definitions/useGameMeritLadders';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { card } from '../../typography';
import GameTile from '@/features/game-selection/GameTile';
import GroupedGameGrid from '@/features/game-selection/GroupedGameGrid';
import MeritDistributionBar from '@/features/game-selection/MeritDistributionBar';
import { cycleWeightPercent, normalizeWeight } from '@/features/game-selection/weights';
import { resolveSelectedRuleSetId } from '@/features/game-selection/ruleSets';
import SectionHeading from '../shared/SectionHeading';
import { TOUR_EDIT_MEDIA_PAGE_PATH } from '../../constants';
import type { GameDefinition } from '../../types';

type Props = {
  enabledGameDefinitions: GameDefinition[];
  tournamentGameIds: string[];
  weightsByGameId: Record<string, number>;
  ruleSetByGameId?: Record<string, string>;
  playerCount: number;
  roundCount?: number;
  onToggleGame: (gameIds: string[]) => void;
  onWeightChange: (gameId: string, weightPercent: number) => void;
  onRuleSetChange?: (gameId: string, ruleSetId: string) => void;
};

const FALLBACK_MERIT_PLAYER_COUNT = 4;

export default function TourGamesEditor({
  enabledGameDefinitions,
  tournamentGameIds,
  weightsByGameId,
  ruleSetByGameId,
  playerCount,
  roundCount = 1,
  onToggleGame,
  onWeightChange,
  onRuleSetChange,
}: Props) {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { user } = useUser();
  const { t } = useI18n();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoGame, setInfoGame] = useState<GameDefinition | null>(null);

  const tournamentSet = useMemo(() => new Set(tournamentGameIds), [tournamentGameIds]);
  const meritPlayerCount = playerCount > 1 ? playerCount : FALLBACK_MERIT_PLAYER_COUNT;
  // The ladders arrive resolved per preset weight, with the tour's round count already folded
  // into the tour-long games (Freeze, Eclectic) and projected over every other game's totals.
  const extraMeritWeights = useMemo(() => Object.values(weightsByGameId), [weightsByGameId]);
  const meritLadders = useGameMeritLadders(apiBaseUrl, user?.token, meritPlayerCount, roundCount, extraMeritWeights);

  const getWeightPercent = useCallback(
    (game: GameDefinition) => weightsByGameId[game.id] ?? normalizeWeight(game.defaultWeightPercent),
    [weightsByGameId],
  );

  const cycleWeight = useCallback(
    (game: GameDefinition) => {
      onWeightChange(game.id, cycleWeightPercent(getWeightPercent(game)));
    },
    [getWeightPercent, onWeightChange],
  );

  const getTourLadder = useCallback(
    (game: GameDefinition) => ladderForWeight(meritLadders[game.id], getWeightPercent(game)),
    [getWeightPercent, meritLadders],
  );

  const meritDistributionEntries = useMemo(
    () =>
      enabledGameDefinitions
        .filter((game) => tournamentSet.has(game.id))
        .map((game) => ({ game, row: meritRowForWeight(meritLadders[game.id], getWeightPercent(game)) }))
        // Achievement and hazard games pay per unpredictable occurrence, so the backend has no
        // tour total for them and they stay out of the projection.
        .filter(({ row }) => row !== null && row.tourTotal !== null)
        .map(({ game, row }) => ({
          gameId: game.id,
          name: getGameDisplayName(game),
          ladder: row!.ladder,
          winnerTotal: row!.winnerTotal,
          tourTotal: row!.tourTotal,
        })),
    [enabledGameDefinitions, getWeightPercent, meritLadders, tournamentSet],
  );

  const handleToggle = useCallback(
    (gameId: string) => {
      if (tournamentSet.has(gameId)) {
        onToggleGame(tournamentGameIds.filter((id) => id !== gameId));
      } else {
        onToggleGame([...tournamentGameIds, gameId]);
      }
    },
    [onToggleGame, tournamentGameIds, tournamentSet],
  );

  const openInfo = useCallback((game: GameDefinition) => {
    setInfoGame(game);
    setInfoOpen(true);
  }, []);

  // The setup modal shows the same ladder the tile does, resolved for the weight in force.
  const infoGameMerits = infoGame ? meritLadders[infoGame.id] : undefined;
  const infoGameLadder = infoGame ? getTourLadder(infoGame) : [];

  const renderTile = useCallback(
    (game: GameDefinition) => {
      const selected = tournamentSet.has(game.id);
      const ladder = selected ? getTourLadder(game) : [];
      return (
        <GameTile
          key={game.id}
          game={game}
          selected={selected}
          mediaPagePath={TOUR_EDIT_MEDIA_PAGE_PATH}
          onPress={() => handleToggle(game.id)}
          onInfoPress={() => openInfo(game)}
          meritLadder={ladder}
          perTeam={meritLadders[game.id]?.perTeam ?? false}
          perAchievement={meritLadders[game.id]?.kind === 'perAchievement'}
          perEvent={meritLadders[game.id]?.kind === 'perEvent'}
          weightFixed={meritLadders[game.id] !== undefined && meritLadders[game.id].kind !== 'ladder'}
          weightPercent={getWeightPercent(game)}
        />
      );
    },
    [getTourLadder, getWeightPercent, handleToggle, meritLadders, openInfo, tournamentSet],
  );

  return (
    <View style={styles.container}>
      <SectionHeading title={t('tourEdit.tourGamesTitle')} count={tournamentGameIds.length} />
      <MeritDistributionBar entries={meritDistributionEntries} roundCount={roundCount} />
      {enabledGameDefinitions.length === 0 ? (
        <Text style={styles.emptyText}>{t('tourEdit.gamesEmpty')}</Text>
      ) : (
        <GroupedGameGrid games={enabledGameDefinitions} renderTile={renderTile} />
      )}
      <RoundGameInfoModal
        visible={infoOpen}
        game={infoGame}
        apiBaseUrl={apiBaseUrl}
        onClose={() => setInfoOpen(false)}
        weightPercent={infoGame ? getWeightPercent(infoGame) : undefined}
        onWeightPress={infoGame ? () => cycleWeight(infoGame) : undefined}
        weightFixed={infoGameMerits !== undefined && infoGameMerits.kind !== 'ladder'}
        meritLadder={infoGameLadder}
        perTeam={infoGameMerits?.perTeam ?? false}
        perAchievement={infoGameMerits?.kind === 'perAchievement'}
        perEvent={infoGameMerits?.kind === 'perEvent'}
        selectedRuleSetId={
          infoGame
            ? resolveSelectedRuleSetId({
                ruleSets: infoGame.rules,
                roundRuleSetId: ruleSetByGameId?.[infoGame.id],
              })
            : null
        }
        onSelectRuleSet={
          infoGame && onRuleSetChange ? (ruleSetId) => onRuleSetChange(infoGame.id, ruleSetId) : undefined
        }
        ruleScope="tour"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...card,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
